/**
 * Thin wrapper around the GTM ``dataLayer`` for tracking conversion
 * events.  GTM itself is initialized in src/index.js; events pushed
 * here flow into whatever destinations the GTM container is wired
 * to (GA4, Meta Pixel, Google Ads, etc).
 *
 * The module is the single seam between application code and GTM.
 * Bug or rename in event names → grep one place.  Tests assert
 * against ``window.dataLayer`` directly so a regression in the wire-
 * up surfaces immediately.
 *
 * Conversion events covered (start small; add as new flows ship):
 *   - free_assessment_submitted  — homepage / verify form submission
 *   - checkout_success          — Stripe payment confirmation
 *   - account_created           — first-time customer order
 */

const DATA_LAYER_NAME = "dataLayer";

function pushEvent(payload) {
  // GTM creates ``window.dataLayer`` lazily; tolerate being called
  // before TagManager.initialize() has run (e.g. in early tests or
  // when the script is blocked by an ad-blocker — pushing into a
  // freshly-created array still works once GTM appears later).
  if (typeof window === "undefined") return;
  window[DATA_LAYER_NAME] = window[DATA_LAYER_NAME] || [];
  window[DATA_LAYER_NAME].push(payload);
}

export function trackFreeAssessmentSubmitted({
  county = null,
  isPreOrder = false,
  hasReferral = false,
} = {}) {
  pushEvent({
    event: "free_assessment_submitted",
    county,
    is_pre_order: Boolean(isPreOrder),
    has_referral: Boolean(hasReferral),
  });
}

export function trackCheckoutSuccess({
  orderUuid = null,
  amount = null,
  currency = "USD",
  isSubscription = false,
} = {}) {
  pushEvent({
    event: "checkout_success",
    order_uuid: orderUuid,
    value: amount,
    currency,
    is_subscription: Boolean(isSubscription),
  });
}

export function trackAccountCreated({ orderUuid = null } = {}) {
  pushEvent({
    event: "account_created",
    order_uuid: orderUuid,
  });
}

// Exported for tests.
export const _internal = { DATA_LAYER_NAME, pushEvent };
